import { AxiosResponse } from 'axios'
import { saveAs } from 'file-saver'
import { queryOptions } from '@tanstack/react-query'
import { NDocuments } from '../interfaces/services/documents.interfaces'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { sortStringsAlphabetically } from '../helpers/sorting'
import { IDocumentModel } from '../interfaces/models/documents.interfaces'

export const getPerSocieties = (
  societyIds: string[]
): Promise<AxiosResponse<NDocuments.NGetPerSocieties.IResponse>> => {
  const body: NDocuments.NGetPerSocieties.IRequestBody = {
    societyIds,
  }
  return axiosAuth.post<NDocuments.NGetPerSocieties.IResponse>(
    createUrl('/documents/get-per-societies'),
    body
  )
}

export const getPerSocietyAndFolder = (
  societyId: string,
  folderId: string
): Promise<AxiosResponse<NDocuments.NGetPerSocieties.IResponse>> => {
  return axiosAuth.get<NDocuments.NGetPerSocieties.IResponse>(
    createUrl(`/documents/get-per-societies/${societyId}/${folderId}`)
  )
}

export const downloadDocumentFile = async (
  id: string,
  filename: string
): Promise<void> => {
  const { data } = await axiosAuth.get<Blob>(
    createUrl(`/documents/fetch/${id}`),
    { responseType: 'blob' }
  )
  saveAs(data, filename)
}

export const buildDocumentRequestBody = (
  variables: NDocuments.NCreate.IRequestBody
): NDocuments.NCreate.IRequestBody => {
  const { name, notificationSettings, societyId, filename, data, ...rest } =
    variables
  const body: NDocuments.NCreate.IRequestBody = {
    ...{
      data,
      name,
      filename,
      type: filename.split('.').pop() as IDocumentModel['type'],
    },
    ...(rest.parentId !== undefined && { parentId: rest.parentId }),
    ...(societyId !== undefined && {
      societyId,
    }),
    ...(rest.section !== undefined && { section: rest.section }),
    ...(notificationSettings !== undefined && {
      notificationSettings,
    }),
  }
  return body
}

export const createDocument = (
  body: NDocuments.NCreate.IRequestBody
): Promise<AxiosResponse<NDocuments.NCreate.IResponse>> => {
  return axiosAuth.post<NDocuments.NCreate.IResponse>(
    createUrl('/documents/document'),
    body
  )
}

export const patchDocument = (params: {
  id: string
  body: NDocuments.NPatch.IRequestBody
}): Promise<AxiosResponse<NDocuments.NPatch.IResponse>> => {
  return axiosAuth.patch<NDocuments.NPatch.IResponse>(
    createUrl(`/documents/document/${params.id}`),
    params.body
  )
}

export const deleteDocument = (
  id: string
): Promise<AxiosResponse<NDocuments.NDel.IResponse>> => {
  return axiosAuth.delete<NDocuments.NDel.IResponse>(
    createUrl(`/documents/document/${id}`)
  )
}

export const getDocument = (
  documentId: string
): Promise<AxiosResponse<NDocuments.NGetById.IResponse>> => {
  return axiosAuth.get<NDocuments.NGetById.IResponse>(
    createUrl(`/documents/document/${documentId}`)
  )
}

export const documentQueries = {
  getDocument: (documentId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'documents', entity: 'document', documentId }],
      queryFn: async ({ queryKey }) => {
        return (await getDocument(queryKey[0].documentId!)).data.data
      },
      enabled: !!documentId,
    }),
  getPerSocieties: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'documents', entity: 'document', societyId }],
      queryFn: async ({ queryKey }) => {
        const documents = (await getPerSocieties([queryKey[0].societyId!])).data
          .data
        documents.sort((a, b) => sortStringsAlphabetically(a.name, b.name))
        return documents
      },
      enabled: !!societyId,
    }),
  getPerSocietyAndFolder: (societyId: string, folderId: string) =>
    queryOptions({
      queryKey: [
        { scope: 'documents', entity: 'document', societyId, folderId },
      ],
      queryFn: async ({ queryKey }) => {
        const documents = (
          await getPerSocietyAndFolder(
            queryKey[0].societyId,
            queryKey[0].folderId
          )
        ).data.data
        documents.sort((a, b) => {
          if (a.type === 'folder' && b.type !== 'folder') return -1
          if (a.type !== 'folder' && b.type === 'folder') return 1
          return sortStringsAlphabetically(a.name, b.name)
        })
        return documents
      },
      enabled: !!societyId && !!folderId,
    }),
}
